import {match, Pattern} from 'ts-pattern';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  UserListCsvExport,
  UserListExportTarget,
  useLazyExportAllUserToBuyToCsvQuery,
  useSourcingExportVehiclesToCsvMutation,
} from '@dms/api/core';
import {COUNTRY_CZE, handleApiError} from '@dms/shared';

import {Nullish, pushGtmEvent} from 'shared/web';

import {DEFAULT_CURRENCY_CODE} from '../constants/DEFAULT_CURRENCY_CODE';
import {SourcingUserList} from '../types/SourcingUserList';
import {downloadCsv} from '../utils/downloadCsv';
import {useSourcingCurrency} from './useSourcingCurrency';
import {useSourcingPricingStrategy} from './useSourcingPricingStrategy';
import {useSourcingTargetCountry} from './useSourcingTargetCountry';
import {SourcingType, useSourcingType} from './useSourcingType';

type ExportOptions = {
  /** The selected vehicles; omitted, the whole list is exported. */
  externalIds?: string[];
  /** The list's current sort, so the file reads in the order the user sees. */
  orderBy?: string[] | Nullish;
};

/**
 * Null when there is nothing to export: no user list (the search results), or Carvago buy on an
 * auctions route — that list exists for classifieds only.
 */
const getExportTarget = (sourcingType: SourcingType, list: SourcingUserList | null) =>
  match({sourcingType, list})
    .returnType<UserListExportTarget | null>()
    .with({list: Pattern.nullish}, () => null)
    .with({sourcingType: SourcingType.CLASSIFIEDS, list: Pattern.string}, (target) => ({
      sourcingType: 'CLASSIFIEDS',
      list: target.list,
    }))
    .with({sourcingType: SourcingType.AUCTIONS, list: 'CARVAGO_BUY'}, () => null)
    .with(
      {
        sourcingType: SourcingType.AUCTIONS,
        list: Pattern.union('TO_BUY', 'FAVOURITE', 'COMPARISON', 'HIDDEN'),
      },
      (target) => ({sourcingType: 'AUCTIONS', list: target.list})
    )
    .exhaustive();

/**
 * Exports one of the user's sourcing lists to CSV, for the sourcing type of the current route —
 * the auctions To buy page exports auctions, never the classifieds list.
 *
 * The request carries the currency, buyer country and pricing strategy the list is shown in,
 * so prices and margins in the file match the screen.
 */
export function useExportSourcingUserList(list: SourcingUserList | null) {
  const sourcingType = useSourcingType();
  const currency = useSourcingCurrency();
  const targetCountry = useSourcingTargetCountry();
  const pricingStrategy = useSourcingPricingStrategy();
  const [exportAll, {isFetching: isExportingAll}] = useLazyExportAllUserToBuyToCsvQuery();
  const [exportSelected, {isLoading: isExportingSelected}] =
    useSourcingExportVehiclesToCsvMutation();

  const target = getExportTarget(sourcingType, list);

  const isExporting = isExportingAll || isExportingSelected;

  const handleExport = (options: ExportOptions = {}) => {
    // A second click while the first export runs would download the same file twice.
    if (isNil(target) || isExporting) {
      return;
    }

    const orderBy = options.orderBy?.join(',');
    const args = {
      ...target,
      currency: currency?.code ?? DEFAULT_CURRENCY_CODE,
      buyerCountry: targetCountry?.code ?? COUNTRY_CZE,
      pricingStrategy,
      orderBy: isNilOrEmpty(orderBy) ? undefined : orderBy,
    };
    const request: Promise<UserListCsvExport> = isNil(options.externalIds)
      ? exportAll(args).unwrap()
      : exportSelected({...args, externalIds: options.externalIds}).unwrap();

    const typeAndList = `${sourcingType}_${target.list.toLowerCase()}`;
    // Reached only when Content-Disposition does not reach the browser; the backend's own
    // name also carries the tenant and the date.
    const fallbackFileName = `sourcing-${sourcingType}-${target.list.toLowerCase().replace('_', '-')}.csv`;

    request
      .then((csvExport) => {
        downloadCsv(csvExport.content, csvExport.fileName ?? fallbackFileName);
        pushGtmEvent({
          event: `sourcing_${typeAndList}_exported`,
          event_id: `sourcing_${typeAndList}_exported`,
          params: {
            export_scope: isNil(options.externalIds) ? 'all' : 'selected',
            vehicles_count: options.externalIds?.length,
          },
        });
      })
      .catch(handleApiError);
  };

  return {
    exportUserList: handleExport,
    isExporting,
    canExport: !isNil(target),
  };
}
